import type { DeviceInfo, WifiNetwork } from '../../services/admin';
import { useWifiControls } from './useWifiControls';

// Wi-Fi scan / on / off / connect for ONE radio (documentation/protocol.md "Wi-Fi remote control"),
// shared by Admin > Radios (Wi-Fi modal) and Device Details ("Wifi Network"). Every command carries
// the radio's request id back; the result shown is the radio's answer to THIS request, never an older
// scan or operation. Disabling Wi-Fi and connecting/switching need a visible confirmation. The
// password lives only in this component's state until the request is sent, then it is cleared.

export type WifiPending = 'scan' | 'enabled' | 'connect' | null;

export type WifiConfirm = { kind: 'disable' } | { kind: 'connect'; network: WifiNetwork };

export interface WifiControlsViewProps {
  online: boolean;
  // The radio app's wifi_commands_v2 capability (runs Wi-Fi commands without its UI open); null while
  // unknown (no Device Info read yet).
  backgroundCommands?: boolean | null;
  enabled: boolean | null;
  ssid: string | null;
  signalDbm: number | null;
  linkSpeedMbps: number | null;
  pending: WifiPending;
  message: string | null;
  networks: WifiNetwork[];
  scannedAgo: string | null;
  selected: WifiNetwork | null;
  password: string;
  confirm: WifiConfirm | null;
  onToggle: () => void;
  onScan: () => void;
  onSelect: (network: WifiNetwork | null) => void;
  onPassword: (value: string) => void;
  onConnect: () => void;
  onConfirm: () => void;
  onCancelConfirm: () => void;
}

export function WifiControlsView(p: WifiControlsViewProps) {
  const busy = p.pending !== null || !p.online;
  const needsPassword = p.selected !== null && p.selected.security !== 'Open';
  return (
    <div className="space-y-4">
      {!p.online && <p className="text-sm text-amber-400">Radio is offline. Wi-Fi control is unavailable.</p>}
      {p.online && p.backgroundCommands === false && (
        <p className="text-sm text-amber-400">
          This radio&rsquo;s app version carries out Wi-Fi commands only while the RadioOS app is open on the radio,
          and may not answer otherwise. Update the radio&rsquo;s app for Wi-Fi commands in the background.
        </p>
      )}
      <div className="flex items-center justify-between rounded border border-gray-700 bg-gray-900 p-3">
        <div>
          <p className="font-semibold text-white">
            Wi-Fi {p.enabled === null ? 'state unknown' : p.enabled ? 'enabled' : 'disabled'}
          </p>
          <p className="text-xs text-gray-400">
            {p.enabled ? (p.ssid ? `Connected to ${p.ssid}` : 'Scan and select a network to connect.') : 'Enable Wi-Fi before scanning.'}
          </p>
        </div>
        <button
          type="button"
          onClick={p.onToggle}
          disabled={busy || p.confirm !== null}
          className={`px-3 py-1.5 rounded text-sm font-semibold text-white disabled:opacity-50 ${p.enabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-green-700 hover:bg-green-600'}`}
        >
          {p.pending === 'enabled' ? 'Requesting…' : p.enabled ? 'Disable' : 'Enable'}
        </button>
      </div>

      {p.enabled && (p.signalDbm != null || p.linkSpeedMbps != null) && (
        <div className="grid grid-cols-2 gap-3 rounded border border-gray-700 bg-gray-900 p-3 text-sm">
          <div><p className="text-gray-400">Signal</p><p className="font-semibold text-white">{p.signalDbm != null ? `${p.signalDbm} dBm` : '—'}</p></div>
          <div><p className="text-gray-400">Link speed</p><p className="font-semibold text-white">{p.linkSpeedMbps != null ? `${p.linkSpeedMbps} Mbps` : '—'}</p></div>
        </div>
      )}

      {p.confirm?.kind === 'disable' && (
        <div role="alertdialog" className="rounded border border-amber-600 bg-amber-900/20 p-3 space-y-2">
          <p className="font-semibold text-white">Disable Wi-Fi on this radio?</p>
          <p className="text-sm text-gray-300">
            {p.ssid ? `It will disconnect from ${p.ssid}. ` : ''}The radio stays reachable only over mobile data.
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={p.onCancelConfirm} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold rounded">Cancel</button>
            <button type="button" onClick={p.onConfirm} className="px-3 py-1.5 bg-amber-700 hover:bg-amber-600 text-white text-sm font-semibold rounded">Disable Wi-Fi</button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button type="button" onClick={p.onScan} disabled={!p.enabled || busy} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-400 text-white text-sm font-semibold py-2 px-3 rounded">
          {p.pending === 'scan' ? 'Scanning…' : 'Scan networks'}
        </button>
        {p.message && <p className="text-sm text-gray-400">{p.message}</p>}
      </div>

      {p.networks.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase font-semibold text-gray-400">
            Available networks{p.scannedAgo ? ` · scanned ${p.scannedAgo}` : ''}
          </p>
          {p.networks.map((network) => (
            <button
              key={`${network.ssid}-${network.bssid || ''}`}
              type="button"
              onClick={() => p.onSelect(network)}
              disabled={busy}
              className={`w-full text-left rounded border p-3 disabled:opacity-50 ${p.selected?.ssid === network.ssid ? 'border-sky-500 bg-sky-500/10' : 'border-gray-700 hover:bg-gray-700/60'}`}
            >
              <span className="block font-semibold text-white">{network.ssid}{network.ssid === p.ssid ? ' (current)' : ''}</span>
              <span className="block text-xs text-gray-400">{network.security} · {network.signal_dbm} dBm</span>
            </button>
          ))}
        </div>
      )}

      {p.selected && p.confirm?.kind !== 'connect' && (
        <div className="rounded border border-gray-700 p-4 space-y-3">
          <p className="font-semibold text-white">Connect to {p.selected.ssid}</p>
          {needsPassword && (
            <input
              type="password"
              autoComplete="new-password"
              maxLength={63}
              value={p.password}
              onChange={(event) => p.onPassword(event.target.value)}
              placeholder="Wi-Fi password"
              className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500"
            />
          )}
          {!needsPassword && <p className="text-sm text-gray-400">Open network: no password required.</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => p.onSelect(null)} disabled={p.pending !== null} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white text-sm font-semibold rounded">Cancel</button>
            <button type="button" onClick={p.onConnect} disabled={busy || (needsPassword && p.password.length < 8)} className="px-3 py-1.5 bg-sky-700 hover:bg-sky-600 disabled:opacity-50 text-white text-sm font-semibold rounded">
              {p.pending === 'connect' ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        </div>
      )}

      {p.confirm?.kind === 'connect' && (
        <div role="alertdialog" className="rounded border border-sky-600 bg-sky-900/20 p-3 space-y-2">
          <p className="font-semibold text-white">Connect this radio to {p.confirm.network.ssid}?</p>
          <p className="text-sm text-gray-300">
            {p.ssid && p.ssid !== p.confirm.network.ssid ? `It will leave ${p.ssid}. ` : ''}
            The network is saved on the radio. If the connection fails, the radio stays reachable only over mobile data.
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={p.onCancelConfirm} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold rounded">Cancel</button>
            <button type="button" onClick={p.onConfirm} className="px-3 py-1.5 bg-sky-700 hover:bg-sky-600 text-white text-sm font-semibold rounded">Connect</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WifiControls({ radioId, online, onInfo, info }: {
  radioId: string;
  online: boolean;
  onInfo?: (info: DeviceInfo) => void;
  info?: DeviceInfo | null;
}) {
  return <WifiControlsView {...useWifiControls(radioId, online, onInfo, info)} />;
}
